import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../components/UI/Layouts";
import Content from "../../components/UI/Layouts/Content";
import { LabelType, PushNotification } from "../../types";
import PageHeader from "../../components/common/PageHeader";
import { useTranslation } from "react-i18next";
import { useStores } from "../../hooks/useStores";
import { Breadcrumb, BreadcrumbItem } from "../../components/UI/Breadcrumb";
import { useSnackbar } from "notistack";
import { observer } from "mobx-react";
import Loading from "../../components/common/Loading";
import { VirtualizedTable } from "../../components/common/VirtualizedTable";
import EmptySpace from "../../components/common/EmptySpace";
import PushNotificationModal from "../../components/ScreenPushNotifications/PushNotificationModal";

const PushNotifications: React.FC = observer(() => {
  const { t } = useTranslation();
  const { pushNotificationsStore } = useStores();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const modalHandler = () => {
    setIsModalOpen((prev) => !prev);
  };

  const fetchPushNotifications = useCallback(async () => {
    await pushNotificationsStore.getPushNotifications().catch((err) => {
      enqueueSnackbar(err.message, {
        variant: "error",
      });
    });
  }, [enqueueSnackbar, pushNotificationsStore]);

  const handleSubmit = useCallback(
    async (data: PushNotification) => {
      setIsSubmitting(true);
      try {
        await pushNotificationsStore.addPushNotification(data);
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
        modalHandler();
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [pushNotificationsStore, enqueueSnackbar, t]
  );

  const columns = useMemo(
    () => [
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.pushNotifications.column.id"),
        dataKey: "externalId",
      },
      {
        width: 160,
        flexGrow: 2,
        label: t("screens.pushNotifications.column.title"),
        dataKey: "title",
        render: (titles: LabelType) => (
          <span style={{ whiteSpace: "pre" }}>
            {Object.keys(titles).map((lang) => `${lang}: ${titles[lang]}\n`)}
          </span>
        ),
      },
      {
        width: 160,
        flexGrow: 2,
        label: t("screens.pushNotifications.column.message"),
        dataKey: "message",
        render: (messages: LabelType) => (
          <span style={{ whiteSpace: "pre" }}>
            {Object.keys(messages).map(
              (lang) => `${lang}: ${messages[lang]}\n`
            )}
          </span>
        ),
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.pushNotifications.column.createdAt"),
        dataKey: "createdAt",
        render: (createdAt: Date) => new Date(createdAt).toDateString(),
        sorter: (a: Date, b: Date) => a.getTime() - b.getTime(),
      },
    ],
    [t]
  );

  useEffect(() => {
    fetchPushNotifications();
  }, [fetchPushNotifications]);

  return (
    <Layout hideFooter>
      <Content borderBottomWidth="1px" align="top">
        <Breadcrumb my={"20px"}>
          <BreadcrumbItem path="/">
            {t("screens.pushNotifications.breadcrumb.home")}
          </BreadcrumbItem>
          <BreadcrumbItem active>
            {t("screens.pushNotifications.breadcrumb.pushNotifications")}
          </BreadcrumbItem>
        </Breadcrumb>

        <PageHeader
          title={t("screens.pushNotifications.pageHeader.title")}
          buttonText={t("screens.pushNotifications.pageHeader.buttonText")}
          buttonEnable={true}
          handleButton={modalHandler}
        />
      </Content>
      {pushNotificationsStore.isLoading ? (
        <Loading />
      ) : pushNotificationsStore.pushNotifications.length > 0 ? (
        <Content fullHeight align="top" mt={[null, null, null, "0px"]}>
          <div
            style={{
              flex: "1 1 auto",
              width: "100%",
              height: `${
                (pushNotificationsStore.pushNotifications.length + 1) * 150
              }px`,
              display: "flex",
              marginTop: "20px",
            }}
          >
            <VirtualizedTable
              rowCount={pushNotificationsStore.pushNotifications.length}
              rowGetter={({ index }) =>
                pushNotificationsStore.pushNotifications[index]
              }
              columns={columns}
              rowStyle={(row) =>
                row.index % 2 === 0 ? { background: "#EDF1FB" } : {}
              }
              rowHeight={150}
            />
          </div>
        </Content>
      ) : (
        <EmptySpace
          title={t("screens.pushNotifications.emptyData")}
          buttonEnable={true}
          buttonText={t("screens.pushNotifications.pageHeader.buttonText")}
          handleButton={modalHandler}
        />
      )}

      <PushNotificationModal
        isSubmitting={isSubmitting}
        isOpen={isModalOpen}
        onClose={() => {
          modalHandler();
        }}
        onSubmit={handleSubmit}
      />
    </Layout>
  );
});

export default PushNotifications;
